import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Select } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { virtualClassroomsApi } from "@/features/education-catalog/services/virtual-classrooms.api";
import type { VirtualClassroomFileResDto } from "@/features/education-catalog/types/virtual-classroom.types";
import { getFileDownloadUrl } from "@/lib/files.api";
import styles from "./sections.module.scss";

function formatSize(bytes?: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getExtension(name?: string): string {
  if (!name) return "";
  const idx = name.lastIndexOf(".");
  if (idx < 0 || idx === name.length - 1) return "";
  return name.slice(idx + 1).toLowerCase();
}

export function ClassFilesSection(props: {
  educationId: string;
  /**
   * Eğitmen modu: dolu gelirse bölüm yükleme aksiyonunu da gösterir.
   * Eğitmenin atandığı sınıflar — birden fazlaysa hedef sınıf seçtirilir.
   */
  uploadClassrooms?: Array<{ id: string; name?: string | null }>;
  /** Değiştiğinde liste yeniden çekilir (canlı sınıfta başka birinin yüklemesi). */
  refreshSignal?: number;
  /** Yenilemede yeni gelen belgeleri bildirim olarak göster. */
  announceNewFiles?: boolean;
  /** Bu bölümden belge yüklendikten sonra çağrılır. */
  onUploaded?: () => void;
}) {
  const { educationId, uploadClassrooms, refreshSignal, announceNewFiles, onUploaded } = props;
  const { t } = useTranslation();
  const [files, setFiles] = useState<VirtualClassroomFileResDto[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const canUpload = (uploadClassrooms?.length ?? 0) > 0;
  const [targetClassroomId, setTargetClassroomId] = useState<string | null>(
    uploadClassrooms?.[0]?.id ?? null,
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Son başarılı çekimdeki belgeler; null = henüz ilk yükleme yapılmadı.
  const knownFileIdsRef = useRef<Set<string> | null>(null);

  const fetchFiles = (opts?: {
    cancelledRef?: { current: boolean };
    /** false: kişinin kendi yüklemesi, bildirim gösterme */
    announce?: boolean;
  }) =>
    virtualClassroomsApi
      .listFilesForClientByEducation(educationId)
      .then((items) => {
        if (opts?.cancelledRef?.current) return;
        const known = knownFileIdsRef.current;
        if (announceNewFiles && known && opts?.announce !== false) {
          const added = items.filter((f) => !known.has(f.id));
          if (added.length > 0) {
            toast.info(
              t("liveClassroom.newFiles", {
                names: added
                  .map((f) => f.displayName || f.originalName)
                  .join(", "),
                defaultValue: "Yeni belge paylaşıldı: {{names}}",
              }),
            );
          }
        }
        knownFileIdsRef.current = new Set(items.map((f) => f.id));
        setFiles(items);
        setLoaded(true);
      })
      .catch(() => {
        if (opts?.cancelledRef?.current) return;
        // Yenileme başarısızsa mevcut liste korunur; ilk yüklemede boş gösterilir.
        if (!knownFileIdsRef.current) setFiles([]);
        setLoaded(true);
      });

  useEffect(() => {
    const cancelledRef = { current: false };
    knownFileIdsRef.current = null;
    void fetchFiles({ cancelledRef });
    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [educationId]);

  useEffect(() => {
    if (!refreshSignal) return;
    const cancelledRef = { current: false };
    void fetchFiles({ cancelledRef });
    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  // Yükleme yetkisi yoksa (kursiyer) boş liste render edilmez — eski davranış.
  if (!loaded || (files.length === 0 && !canUpload)) return null;

  const handleDownload = async (
    fileId: string,
    name: string,
  ): Promise<void> => {
    setDownloading(fileId);
    try {
      const url = await getFileDownloadUrl(fileId);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      // apiClient toast
    } finally {
      setDownloading(null);
    }
  };

  const handleUploadPicked = async (picked: File[]): Promise<void> => {
    const classroomId = targetClassroomId ?? uploadClassrooms?.[0]?.id;
    if (!classroomId || picked.length === 0) return;
    setUploading(true);
    try {
      await virtualClassroomsApi.uploadAndBindFilesAsInstructor(
        classroomId,
        picked,
      );
      toast.success(t("classFilesSection.uploadSuccess"));
      await fetchFiles({ announce: false });
      onUploaded?.();
    } catch {
      // apiClient toast
    } finally {
      setUploading(false);
    }
  };

  return (
    <section id="classFiles" className={styles.section}>
      <div className={styles.header}>
        <h2>{t("classFilesSection.title")}</h2>
        <span className={styles.headerCount}>
          {t("classFilesSection.fileCount", { count: files.length })}
        </span>
        {canUpload && (
          <span className={styles.headerActions}>
            {(uploadClassrooms?.length ?? 0) > 1 && (
              <Select
                size="small"
                style={{ minWidth: 160 }}
                value={targetClassroomId}
                onChange={(v: string) => setTargetClassroomId(v)}
                options={(uploadClassrooms ?? []).map((c, i) => ({
                  value: c.id,
                  label:
                    c.name?.trim() ||
                    t("classFilesSection.classroomFallback", { index: i + 1 }),
                }))}
              />
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                const picked = Array.from(e.target.files ?? []);
                e.target.value = "";
                void handleUploadPicked(picked);
              }}
            />
            <Button
              type="primary"
              size="small"
              icon={<UploadOutlined />}
              loading={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {t("classFilesSection.upload")}
            </Button>
          </span>
        )}
      </div>
      {files.length === 0 ? (
        <p className={styles.resourceMeta}>{t("classFilesSection.empty")}</p>
      ) : (
        <div className={styles.resources}>
          {files.map((f) => {
            const displayName = f.displayName || f.originalName;
            const ext = getExtension(f.originalName);
            const sizeText = formatSize(f.size);
            return (
              <div key={f.id} className={styles.resourceRow}>
                <div className={styles.resourceLeft}>
                  <span className={styles.fileBadge} data-ext={ext} aria-hidden>
                    {ext ? ext.toUpperCase().slice(0, 4) : "FILE"}
                  </span>
                  <div className={styles.resourceInfo}>
                    <span className={styles.resourceName}>
                      {displayName ?? t("classFilesSection.fileFallback")}
                    </span>
                    {(ext || sizeText) && (
                      <span className={styles.resourceMeta}>
                        {ext && <span>{ext.toUpperCase()}</span>}
                        {ext && sizeText && (
                          <span className={styles.resourceMetaDot} />
                        )}
                        {sizeText && <span>{sizeText}</span>}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  type="link"
                  className={styles.downloadButton}
                  icon={<DownloadOutlined />}
                  loading={downloading === f.fileID}
                  onClick={() => void handleDownload(f.fileID, f.originalName)}
                >
                  {t("classFilesSection.download")}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
